<template>
  <div class="w-full h-screen relative bg-blue-50 overflow-hidden">
    <div class="flex justify-center pt-4">
      <img src="/junkanlogo_transparent_v3.png" class="max-w-sm" />
    </div>
    <div class="max-w-md mx-auto mt-6 p-8 bg-white rounded-xl shadow-lg border border-blue-100">
      <div class="text-center mb-6">
        <h1 class="text-2xl font-bold text-gray-900">Login</h1>
      </div>

      <div v-if="error" class="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-lg text-sm">
        {{ error }}
      </div>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input v-model="email" type="email" placeholder="mail@example.com" @input="clearError" class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-[#00225E] focus:border-[#00225E] sm:text-sm" />
        </div>
        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <div class="relative">
            <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="********" @input="clearError" class="block w-full px-4 py-2 border rounded-md shadow-sm focus:ring-[#00225E] focus:border-[#00225E] sm:text-sm" />
            <button type="button" @click="toggleShowPassword" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#00225E]">{{ showPassword ? 'Hide' : 'Show' }}</button>
          </div>
        </div>
        <div class="space-y-3 pt-2">
          <button @click="submit" :disabled="loading" class="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#00225E] hover:bg-[#00225E]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00225E] disabled:opacity-50 disabled:cursor-not-allowed">
            {{ loading ? 'Logging in...' : 'Login' }}
          </button>
          
          <div class="text-center">
            <button @click="forgotPassword" class="text-sm text-[#00225E] hover:text-[#00225E]/80 underline">
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>

    <ChangePasswordModal 
      :show="showChangePasswordModal" 
      :email="email"
      @close="showChangePasswordModal = false"
      @success="handlePasswordChangeSuccess"
    />
  </div>
  
</template>

<script>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import ChangePasswordModal from "../components/ChangePasswordModal.vue";

export default {
  components: {
    ChangePasswordModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const email = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const showChangePasswordModal = ref(false);
    const error = ref("");
    const loading = ref(false);
    
    const toggleShowPassword = () => { 
      showPassword.value = !showPassword.value; 
    };

    const clearError = () => {
      if (error.value) {
        error.value = "";
      }
    };
    
    const submit = async () => {
      if (!email.value || !password.value) {
        error.value = "Please enter both email and password.";
        return;
      }

      loading.value = true;
      error.value = "";
      
      try {
        const resp = await store.dispatch("loginUser", { 
          email: email.value, 
          password: password.value 
        });
        
        if (resp.ok) {
          if (resp.mustChangePassword) {
            showChangePasswordModal.value = true;
          } else {
            router.push({ name: "Home" });
          }
        } else {
          if (resp.error === "User not found") {
            error.value = "No account found with this email address.";
          } else if (resp.error === "Incorrect password") {
            error.value = "Incorrect password. Please try again.";
          } else if (resp.error === "Email and password are required") {
            error.value = "Please enter both email and password.";
          } else {
            error.value = resp.error || "Login failed. Please try again.";
          }
        }
      } catch (err) {
        console.error("Login error:", err);
        error.value = "Network error. Please check your connection and try again.";
      } finally {
        loading.value = false;
      }
    };
    
    const handlePasswordChangeSuccess = () => {
      showChangePasswordModal.value = false;
      error.value = "";
      router.push({ name: "Home" });
    };
    
    const forgotPassword = () => { 
      router.push({ name: "RecoverPassword" }); 
    };
    
    // Auto-populate fields from URL parameters
    onMounted(() => {
      const urlEmail = route.query.email;
      const urlPassword = route.query.password;
      
      if (urlEmail) {
        email.value = decodeURIComponent(urlEmail);
      }
      
      if (urlPassword) {
        password.value = decodeURIComponent(urlPassword);
        // Show password when auto-populated
        showPassword.value = true;
      }
    });
    
    return { 
      email, 
      password, 
      submit, 
      showPassword, 
      toggleShowPassword, 
      forgotPassword,
      showChangePasswordModal,
      handlePasswordChangeSuccess,
      error,
      loading,
      clearError
    };
  },
};
</script>


